import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { NEW_BRAND_ADDED } from 'constants/mixpanel';
import { updateBrand } from 'libs/e1api/autoBrand';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppDispatch } from 'modules/Editor/store';
import { setBrandIdx } from 'modules/Editor/store/editorSettingsSlice';
import { useBrand } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { ChangeEvent, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseFiniteNumberWithNaN } from 'utils/number';

interface AddBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const AddBrandModal = memo(({ isOpen, onClose }: AddBrandModalProps) => {
  return (
    <Modal
      id={'add-new-brand'}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnEsc
      closeOnOverlayClick
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <CommonGatedUpgradeModalContent onClose={onClose} />
    </Modal>
  );
});

AddBrandModal.displayName = 'AddBrandModal';

const CommonGatedUpgradeModalContent = ({ onClose }: { onClose: () => void }) => {
  const [name, setName] = useState('');
  const toast = useToast();
  const dispatch = useAppDispatch();
  const userBrand = useBrand();
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });

  const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const onSetBrandIdx = (value: string) => {
    const idx = parseFiniteNumberWithNaN(value);
    dispatch(setBrandIdx(idx));
  };

  const createBrand = async () => {
    const newBrand = await updateBrand({
      name,
    });

    if (newBrand.status === 'fail') {
      toast({
        title: newBrand.message,
        status: 'error',
      });
      return;
    }

    Mixpanel.track(NEW_BRAND_ADDED.eventName);

    toast({
      title: t('addedMessage'),
    });
    userBrand.refresh();
    // select the new brand
    onSetBrandIdx(String(userBrand?.data?.length || 0));
    onClose();
  };

  return (
    <ModalContent>
      <ModalCloseButton data-testid={'new-brand-close-button'} />
      <ModalHeader>{t('addNew')}</ModalHeader>
      <ModalBody>
        <Flex flexDir={'row'}>
          <Input
            id='toolbar-link-input'
            size='sm'
            border='1px solid'
            borderColor='gray.300'
            value={name}
            onChange={onChangeName}
            data-testid='name-brand-input'
            placeholder={t('nameYourBrand')}
          />
        </Flex>
      </ModalBody>
      <ModalFooter gap='2'>
        <Button size='sm' variant='ghost' onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button size='sm' variant='action' onClick={createBrand} disabled={!name.trim()}>
          {t('create')}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
